// ═══════════════════════════════════════════════════════════════
// EVENT SERVICE — Supabase CRUD for chapter_events + event_attendance
// Path: src/services/eventService.js
//
// Follows same patterns as memberService.js and contactLogService.js
//
// v1.5.2 Changes:
//   - getAll() returns member_count + prospect_count from event record
//   - create() no longer accepts guest_count
//   - Removed guest_count references throughout
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

export const eventService = {
  // ─── Events CRUD ───────────────────────────────────────────

  /**
   * Get all chapter events, newest first.
   * Returns member_count and prospect_count from the event record.
   */
  async getAll() {
    const { data, error } = await supabase
      .from("chapter_events")
      .select("*")
      .order("event_date", { ascending: false });

    if (error) {
      debugLog.error("eventService.getAll", error);
      throw error;
    }

    return (data || []).map((event) => ({
      ...event,
      member_count: event.member_count || 0,
      prospect_count: event.prospect_count || 0,
    }));
  },

  /**
   * Create a new event. Returns the created event record.
   */
  async create(eventData) {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from("chapter_events")
      .insert({
        name: eventData.name,
        event_date: eventData.event_date,
        event_type: eventData.event_type || "other",
        description: eventData.description || null,
        member_count: 0,
        prospect_count: 0,
        created_by: user?.id || null,
      })
      .select()
      .single();

    if (error) {
      debugLog.error("eventService.create", error);
      throw error;
    }

    debugLog.action("Event created", { name: data.name, id: data.id });
    return { ...data, member_count: 0, prospect_count: 0 };
  },

  /**
   * Update an existing event's fields.
   */
  async update(eventId, updates) {
    const { data, error } = await supabase
      .from("chapter_events")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", eventId)
      .select()
      .single();

    if (error) {
      debugLog.error("eventService.update", error);
      throw error;
    }

    return data;
  },

  /**
   * Delete an event. CASCADE will remove attendance records automatically.
   */
  async remove(eventId) {
    const { error } = await supabase
      .from("chapter_events")
      .delete()
      .eq("id", eventId);

    if (error) {
      debugLog.error("eventService.remove", error);
      throw error;
    }

    debugLog.action("Event deleted", { id: eventId });
  },

  // ─── Attendance CRUD ───────────────────────────────────────

  /**
   * Get all attendance records for an event.
   * Returns array of { member_id, attended }.
   */
  async getAttendance(eventId) {
    const { data, error } = await supabase
      .from("event_attendance")
      .select("id, member_id, attended")
      .eq("event_id", eventId);

    if (error) {
      debugLog.error("eventService.getAttendance", error);
      throw error;
    }

    return data || [];
  },

  /**
   * Save attendance for an event.
   *
   * Compares current DB records against the new checkedMemberIds set.
   * - Adds records for newly checked members
   * - Removes records for unchecked members
   *
   * Returns { added: number, removed: number, removedMemberIds: string[] }
   */
  async saveAttendance(eventId, checkedMemberIds) {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    // Get existing attendance
    const existing = await this.getAttendance(eventId);
    const existingIds = new Set(existing.map((a) => a.member_id));
    const checkedSet = new Set(checkedMemberIds);

    // Determine adds and removes
    const toAdd = checkedMemberIds.filter((id) => !existingIds.has(id));
    const toRemove = existing.filter((a) => !checkedSet.has(a.member_id));

    // Insert new attendance records
    if (toAdd.length > 0) {
      const rows = toAdd.map((memberId) => ({
        event_id: eventId,
        member_id: memberId,
        attended: true,
        checked_by: user?.id || null,
      }));

      const { error } = await supabase.from("event_attendance").insert(rows);

      if (error) {
        debugLog.error("eventService.saveAttendance insert", error);
        throw error;
      }
    }

    // Remove unchecked members
    if (toRemove.length > 0) {
      const removeIds = toRemove.map((a) => a.id);
      const { error } = await supabase
        .from("event_attendance")
        .delete()
        .in("id", removeIds);

      if (error) {
        debugLog.error("eventService.saveAttendance delete", error);
        throw error;
      }
    }

    debugLog.action("Attendance saved", {
      eventId,
      added: toAdd.length,
      removed: toRemove.length,
      total: checkedMemberIds.length,
    });

    return {
      added: toAdd.length,
      removed: toRemove.length,
      removedMemberIds: toRemove.map((a) => a.member_id),
    };
  },

  // ─── Backup/Restore Support ────────────────────────────────

  /**
   * Export all events and attendance for backup.
   * Returns { events: [...], attendance: [...] }
   */
  async exportAll() {
    const { data: events, error: evErr } = await supabase
      .from("chapter_events")
      .select("*")
      .order("event_date", { ascending: false });

    if (evErr) {
      debugLog.error("eventService.exportAll events", evErr);
      throw evErr;
    }

    const { data: attendance, error: attErr } = await supabase
      .from("event_attendance")
      .select("*");

    if (attErr) {
      debugLog.error("eventService.exportAll attendance", attErr);
      throw attErr;
    }

    return {
      events: events || [],
      attendance: attendance || [],
    };
  },

  /**
   * Import events and attendance from backup data.
   * Clears existing data first, then restores.
   * Returns { eventsRestored, attendanceRestored }
   */
  async importAll(backupData) {
    const { events = [], attendance = [] } = backupData;

    if (events.length === 0) {
      debugLog.data("No events to restore");
      return { eventsRestored: 0, attendanceRestored: 0 };
    }

    // Clear existing attendance first (FK dependency), then events
    const { error: clearAttErr } = await supabase
      .from("event_attendance")
      .delete()
      .neq("id", "00000000-0000-0000-0000-000000000000");

    if (clearAttErr) {
      debugLog.error("eventService.importAll clear attendance", clearAttErr);
      throw clearAttErr;
    }

    const { error: clearEvErr } = await supabase
      .from("chapter_events")
      .delete()
      .neq("id", "00000000-0000-0000-0000-000000000000");

    if (clearEvErr) {
      debugLog.error("eventService.importAll clear events", clearEvErr);
      throw clearEvErr;
    }

    // Restore events
    const { error: insEvErr } = await supabase
      .from("chapter_events")
      .insert(events);

    if (insEvErr) {
      debugLog.error("eventService.importAll insert events", insEvErr);
      throw insEvErr;
    }

    // Restore attendance (only if events succeeded)
    let attendanceRestored = 0;
    if (attendance.length > 0) {
      const { error: insAttErr } = await supabase
        .from("event_attendance")
        .insert(attendance);

      if (insAttErr) {
        debugLog.error("eventService.importAll insert attendance", insAttErr);
        debugLog.warn(
          "eventService.importAll",
          "Events restored but attendance failed"
        );
      } else {
        attendanceRestored = attendance.length;
      }
    }

    debugLog.data("Events restored from backup", {
      events: events.length,
      attendance: attendanceRestored,
    });

    return {
      eventsRestored: events.length,
      attendanceRestored,
    };
  },
};
